// Teilnehmertabelle, die der Abrechnungs-Wizard (Schritt 1) und das Teilnehmer-Modal der
// VA-Übersicht gemeinsam benutzen — eine Tabelle statt zwei, damit beide Ansichten bei
// Spalten und Flags nicht auseinanderlaufen.
//
// Rein darstellend: die Zeilen werden vom Aufrufer gebaut (Wizard aus dem Entwurf, Modal aus
// der Ticketliste), ebenso die Storno-Aktion samt Bestätigungsdialog.

import { useTranslation } from 'react-i18next';
import { Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusMark } from '@/components/ui/status-mark';

export interface ParticipantRow {
  id: string;
  /** Hauptzeile: Kundennummer bzw. „Anonym". */
  label: string;
  /** Zweite, kleinere Zeile, z. B. die E-Mail-Adresse. */
  sublabel?: string;
  /** Hinweise hinter dem Namen, z. B. „manuell erfasst". */
  notes?: string[];
  reserved: boolean;
  sold: boolean;
  redeemed: boolean;
  cancelled: boolean;
  /** Fehlt die Aktion, bleibt die Zelle leer (z. B. für Partner bei echten Tickets). */
  action?: { title: string; onClick: () => void };
}

interface Props {
  rows: ParticipantRow[];
  /** Blendet die Aktionsspalte ganz aus (nur lesende Ansicht). */
  showActions: boolean;
  emptyText: string;
}

export function ParticipantsTable({ rows, showActions, emptyText }: Props) {
  const { t } = useTranslation('events');

  return (
    <div className="rounded-md border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40 text-left text-xs font-medium text-muted-foreground">
            <th className="w-10 py-1.5 pl-3">{t('refund.participants.colNumber')}</th>
            <th className="py-1.5">{t('refund.participants.colName')}</th>
            <th className="w-24 py-1.5 text-center">{t('refund.participants.colReserved')}</th>
            <th className="w-24 py-1.5 text-center">{t('refund.participants.colPaid')}</th>
            <th className="w-24 py-1.5 text-center">{t('refund.participants.colCancelled')}</th>
            <th className="w-28 py-1.5 text-center">{t('refund.participants.colAttended')}</th>
            {showActions && <th className="w-10 py-1.5 pr-2" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.id}
              className={
                row.cancelled
                  ? 'border-b text-muted-foreground line-through last:border-b-0'
                  : 'border-b last:border-b-0'
              }
            >
              <td className="w-10 py-1.5 pl-3 text-muted-foreground no-underline">{index + 1}</td>
              <td className="py-1.5">
                <div>
                  {row.label}
                  {row.notes?.map((note) => (
                    <span key={note} className="ml-2 text-xs text-muted-foreground no-underline">
                      ({note})
                    </span>
                  ))}
                </div>
                {row.sublabel && (
                  <div className="text-xs text-muted-foreground no-underline">{row.sublabel}</div>
                )}
              </td>
              <td className="w-24 py-1.5 text-center">
                <StatusMark value={row.reserved} label={t('refund.participants.colReserved')} />
              </td>
              <td className="w-24 py-1.5 text-center">
                <StatusMark value={row.sold} label={t('refund.participants.colPaid')} />
              </td>
              <td className="w-24 py-1.5 text-center">
                <StatusMark value={row.cancelled} label={t('refund.participants.colCancelled')} />
              </td>
              <td className="w-28 py-1.5 text-center">
                <StatusMark value={row.redeemed} label={t('refund.participants.colAttended')} />
              </td>
              {showActions && (
                <td className="w-10 py-1.5 pr-2 text-right">
                  {row.action && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={row.cancelled}
                      className={
                        row.cancelled
                          ? 'text-muted-foreground/50'
                          : 'text-destructive hover:text-destructive'
                      }
                      onClick={() => !row.cancelled && row.action?.onClick()}
                      title={row.action.title}
                      aria-label={row.action.title}
                    >
                      <span className="relative inline-flex">
                        <Ban className="h-4 w-4" />
                        {row.cancelled && (
                          <span className="absolute inset-0 flex items-center" aria-hidden="true">
                            <span className="h-px w-full rotate-45 bg-current" />
                          </span>
                        )}
                      </span>
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={showActions ? 7 : 6}
                className="py-3 text-center text-muted-foreground"
              >
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
